/**
 * Cascade helpers — recursive descendant updates for parentPath & realmId changes.
 *
 * Uses parentPath.startsWith() to find ALL descendants in a single query.
 * INTERNAL — not exported from operations/index.ts.
 */

import type { Transaction } from "dexie"
import { getNow } from "../runtime.js"

/**
 * Update parentPath and realmId for ALL descendants of an object
 * when it moves to a new parent.
 *
 * @param objectId - The object being moved
 * @param oldParentPath - Its previous parentPath
 * @param newParentPath - Its new parentPath
 * @param newRealmId - The realmId to apply (undefined = keep existing)
 * @param tx - Active Dexie transaction
 * @returns Number of descendants updated
 */
export async function cascadeDescendants(
  objectId: string,
  oldParentPath: string,
  newParentPath: string,
  newRealmId: string | undefined,
  tx: Transaction,
): Promise<number> {
  const oldPrefix = `${oldParentPath}/${objectId}`
  const newPrefix = `${newParentPath}/${objectId}`

  const descendants = await tx
    .table("totodo")
    .where("parentPath")
    .startsWith(oldPrefix)
    .toArray()

  if (descendants.length === 0) return 0

  const timestamp = getNow()

  const updates = descendants.map((d: { id: string; parentPath: string }) => ({
    key: d.id,
    changes: {
      parentPath: d.parentPath.replace(oldPrefix, newPrefix),
      ...(newRealmId !== undefined ? { realmId: newRealmId } : {}),
      updatedAt: timestamp,
    },
  }))

  await tx.table("totodo").bulkUpdate(updates)
  return descendants.length
}
